import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ViewContainerRef,
  DestroyRef
} from '@angular/core';
import { PlatformInformationService } from '../../../../../projects/platform-manager/src/lib/services/platform-information.service';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { MicrositeInterface } from '../../../../../projects/platform-manager/src/lib/interfaces/microsite.interface';
import { ResolutionService } from '../../../core/services/resolution.service';
import { Router, RouterModule } from '@angular/router';
import { ComponentDisplayService } from '../../../core/services/component-display.service';
import { FLY_IN_OUT_ANIMATION } from '../../../core/animations/FLY_IN_OUT_ANIMATION';
import { MENU_FLY_IN_OUT } from '../../../core/animations/MENU_FLY_IN_OUT';
import { MenuService } from '../../../shared/services/menu.service';
import { HelpCenterService } from '../../../pages/help-center/services/help-center.service';
import { provideIcons, basket, phone } from '../../../shared/icons';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrencySwitcherComponent } from './currency-switcher/currency-switcher.component';
import { LanguageSwitcherComponent } from './language-switcher/language-switcher.component';
import { IconComponent } from '../../../shared/icons';
import { LocalizeRouterModule } from '@gilsdav/ngx-translate-router';
import { LogoWithPartnerRefererComponent } from './logo-with-partner-referer/logo-with-partner-referer.component';
import { CurrencyService } from '../../../core/services/currency.service';
import { DeferLoadModule } from '@trademe/ng-defer-load';
import { FOLD_OUT_ANIMATION } from '../../../core/animations/FOLD_OUT_ANIMATION';
import { GeoIpSwitcherComponent } from './geo-ip-switcher/geo-ip-switcher.component';
import { AlpitranslatePipe } from '../../../core/pipes/alpitranslate.pipe';
import { PlatformassetPipe } from '../../../../../projects/platform-manager/src/public_api';
import { loaderMap } from './translations/loader-map';
import { CrowdinTranslationService } from '../../services/crowdin-translation.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  animations: [MENU_FLY_IN_OUT, FLY_IN_OUT_ANIMATION, FOLD_OUT_ANIMATION],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideIcons({ basket, phone })],
  imports: [
    LogoWithPartnerRefererComponent,
    IconComponent,
    AlpitranslatePipe,
    PlatformassetPipe,
    RouterModule,
    LocalizeRouterModule,
    DeferLoadModule,
    //Lazy loaded
    CurrencySwitcherComponent,
    LanguageSwitcherComponent,
    GeoIpSwitcherComponent
  ]
})
export class HeaderComponent implements OnInit {
  showMenu = this.menuService.currentState();
  showAdditionalElements = false;
  currentLang = this.translateService.currentLang;
  displayHelpCenter = this.helpCenterService.displayHelpCenter;
  isPartnersite = this.platformInformationService.getCurrentPartner() !== null;
  variation_logo = '';
  variation_prefix = '';
  variation_logo_addon = '';
  variation_logo_addon_class = '';
  isBrowser = false;
  hotlines = new Map<string, string>();
  platformcode = this.platformInformationService.getCurrentPlatformShortcode();
  hotline = '';
  showCurrencySwitcher = true;
  showLanguageSwitcher = ['ar', 'es'].includes(this.platformcode);
  isMobile = this.resolutionService.isSmallerThanLargeDevice();
  geoIpCountry: string = null;
  navigationInitialized = false;

  isCurrencySwitcherOpen = false;
  isLangSwitcherOpen = false;

  @ViewChild('helpCenter', { read: ViewContainerRef })
  helpCenter: ViewContainerRef;
  @ViewChild('geoIpSwitcher', { read: ViewContainerRef })
  geoIpSwitcher: ViewContainerRef;
  @ViewChild('desktopNav', { read: ViewContainerRef }) desktopNav: ViewContainerRef;
  @ViewChild('langSection', { read: ViewContainerRef }) langSection: ViewContainerRef;
  protected acceptedCurrencies: string[];
  protected acceptedLanguages: string[];

  constructor(
    public platformInformationService: PlatformInformationService,
    public translateService: TranslateService,
    protected resolutionService: ResolutionService,
    private cdr: ChangeDetectorRef,
    protected router: Router,
    protected componentDisplayService: ComponentDisplayService,
    private menuService: MenuService,
    private helpCenterService: HelpCenterService,
    private destroyRef: DestroyRef,
    protected currencyService: CurrencyService,
    private crowdinTranslationService: CrowdinTranslationService
  ) {}

  ngOnInit() {
    this.crowdinTranslationService.watchLangChange({
      loaderMap: loaderMap,
      destroyRef: this.destroyRef,
      successCallback: () => {
        this.cdr.detectChanges();
      }
    });
    this.helpCenterService.toggledHelpCenterEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (event: boolean) => {
        this.displayHelpCenter = event;
        if (event) {
          // asynchronously load the component and inject it into the template.
          // this whole step will need to be rewritten for Ang13 and more or less dropped with Ang14 standalone components
          const { HelpCenterSidebarComponent } = await import(
            '../../../pages/help-center/help-center-page/help-center-sidebar.component'
          );
          this.helpCenter.clear();
          this.helpCenter.createComponent(HelpCenterSidebarComponent);
          this.cdr.markForCheck();
        } else {
          this.helpCenter.clear();
        }
      });
    this.menuService.onMenuStateChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(state => {
      this.showMenu = state;
      this.cdr.markForCheck();
    });
    this.showCurrencySwitcher = this.componentDisplayService.showCurrencySwitcher;
    this.currencyService.onCurrencyChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.cdr.markForCheck();
    });
    this.componentDisplayService.onShowCurrencySwitcherChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(show => {
        this.showCurrencySwitcher = show;
        this.cdr.markForCheck();
      });
    this.showLanguageSwitcher = !this.componentDisplayService.showLanguageSwitcher
      ? false
      : ['ar', 'es'].includes(this.platformcode);
    this.componentDisplayService.onShowLanguageSwitcherChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(show => {
        this.showLanguageSwitcher = !show ? false : ['ar', 'es'].includes(this.platformcode);
        this.cdr.markForCheck();
      });
    if (this.platformInformationService.getIsBrowser()) {
      this.translateService.onLangChange
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((event: LangChangeEvent) => {
          this.currentLang = event.lang;
          this.hotline = this.hotlines.get(event.lang);
          this.cdr.markForCheck();
        });
      this.showAdditionalElements =
        this.resolutionService.getCurrentSizeReference() > this.resolutionService.extraSmallReference;
      this.resolutionService.sizeReferenceChange
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(currentSizeReference => {
          this.showAdditionalElements = currentSizeReference > this.resolutionService.extraSmallReference;
          this.isMobile = this.resolutionService.isSmallerThanLargeDevice();
          if (!this.isMobile) {
            this.menuService.close();
          }
          this.cdr.markForCheck();
        });
      this.isBrowser = true;
    }

    if (this.platformInformationService.micrositeConfig) {
      this.setMicrositeInfo(this.platformInformationService.micrositeConfig);
    }
    this.platformInformationService.datMicro
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((config: MicrositeInterface) => {
        if (config) {
          this.setMicrositeInfo(config);
          this.cdr.markForCheck();
        }
      });
    setTimeout(() => {
      this.platformInformationService.customerCountryCode$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((customerCountryCode: string) => {
          this.geoIpCountry = customerCountryCode;
          if (this.geoIpCountry === 'FR' && this.hotline) {
            this.hotline = this.hotline.replace('+33 ', '0');
          }
          this.cdr.markForCheck();
        });
      this.platformInformationService.data$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
        for (const lang in data.hotlines) {
          if (Object.prototype.hasOwnProperty.call(data.hotlines, lang)) {
            this.hotlines.set(lang, data.hotlines[lang]);
            this.hotline = this.hotlines.get(this.currentLang);
          }
        }
        this.acceptedCurrencies = data.acceptedCurrencies;
        this.acceptedLanguages = data.acceptedLanguages;
        this.cdr.markForCheck();
      });
    });
  }

  async initNav() {
    setTimeout(async () => {
      const { NavigationComponent } = await import('./navigation/navigation.component');
      this.desktopNav?.clear();
      this.desktopNav?.createComponent(NavigationComponent);
      this.cdr.markForCheck();
    });
  }

  setMicrositeInfo(config: MicrositeInterface) {
    this.variation_logo = config.config.logopath;
    this.variation_prefix = config.config.variation_prefix; // /partner-/sport-brugger/cart
    if (config.config.variation_logo_addon) {
      this.variation_logo_addon = `/${config.config.assetpath}/images/${config.config.variation_logo_addon}`;
      this.variation_logo_addon_class = config.config.variation_logo_addon_class;
    }
    this.isPartnersite = true;
    this.cdr.markForCheck();
  }

  toggleMenu() {
    this.initNav();
    if (this.showMenu) {
      this.menuService.close();
    } else {
      this.menuService.open();
    }
  }
}
